/**
 * Spotify integration.
 *
 * Auth: Authorization Code + PKCE - the only OAuth flow appropriate for a
 * browser app (no client secret). Tokens live in localStorage namespaced under
 * wayfarer.* and refresh automatically.
 *
 * Playback: with Premium, the Web Playback SDK turns this PWA into a Spotify
 * Connect device ("Wayfarer"), so audio plays here - on the head unit that
 * means through the car speakers - and our transport buttons drive it. The
 * driver can also keep playing on another device and we remote-control it via
 * the Web API; the controls operate on whatever the active device is.
 */

import { SPOTIFY_CLIENT_ID } from '../core/config';
import type { Ducker } from './voice';

const SCOPES = [
  'streaming',
  'user-read-email',
  'user-read-private',
  'user-read-playback-state',
  'user-modify-playback-state',
  'user-read-currently-playing',
  'user-read-recently-played',
  'playlist-read-private',
].join(' ');

const LS_VERIFIER = 'wayfarer.spotify.verifier';
const LS_TOKENS = 'wayfarer.spotify.tokens';

interface Tokens {
  access_token: string;
  refresh_token: string;
  /** epoch ms */
  expires_at: number;
}

export interface NowPlaying {
  trackName: string;
  artists: string;
  albumArt: string | null;
  durationMs: number;
  positionMs: number;
  paused: boolean;
  /** True when audio is coming out of this app (our SDK device is active). */
  playingHere: boolean;
  deviceName: string | null;
}

export interface SpotifyEvents {
  onState?: (state: NowPlaying | null) => void;
  onAuthChange?: (authed: boolean) => void;
}

declare global {
  interface Window {
    Spotify?: any;
    onSpotifyWebPlaybackSDKReady?: () => void;
  }
}

function redirectUri(): string {
  // Must exactly match a Redirect URI registered on the Spotify app. The
  // dashboard normalises away trailing slashes, so we send the slashless form
  // ("https://airtasker.asia"); the browser lands on "/" either way and
  // completeLoginIfReturning() reads the ?code= from wherever it arrives.
  const url = new URL(import.meta.env.BASE_URL, location.origin).toString();
  return url.replace(/\/$/, '');
}

/* ------------------------------- PKCE ----------------------------------- */

function b64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function sha256(text: string): Promise<Uint8Array> {
  const data = new TextEncoder().encode(text);
  return new Uint8Array(await crypto.subtle.digest('SHA-256', data));
}

export async function beginLogin(): Promise<void> {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(64)));
  localStorage.setItem(LS_VERIFIER, verifier);
  const challenge = b64url(await sha256(verifier));

  const params = new URLSearchParams({
    client_id: SPOTIFY_CLIENT_ID,
    response_type: 'code',
    redirect_uri: redirectUri(),
    scope: SCOPES,
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });
  location.href = `https://accounts.spotify.com/authorize?${params}`;
}

/** Handle ?code= on load. Returns true if we exchanged a code. */
export async function completeLoginIfReturning(): Promise<boolean> {
  const url = new URL(location.href);
  const code = url.searchParams.get('code');
  if (!code) return false;

  url.searchParams.delete('code');
  url.searchParams.delete('state');
  history.replaceState(null, '', url.toString());

  const verifier = localStorage.getItem(LS_VERIFIER);
  if (!verifier) return false;

  const body = new URLSearchParams({
    client_id: SPOTIFY_CLIENT_ID,
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });
  const res = await fetch('https://accounts.spotify.com/api/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) return false;
  const json = await res.json();
  saveTokens({
    access_token: json.access_token,
    refresh_token: json.refresh_token,
    expires_at: Date.now() + json.expires_in * 1000,
  });
  localStorage.removeItem(LS_VERIFIER);
  return true;
}

function loadTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(LS_TOKENS);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

function saveTokens(t: Tokens | null): void {
  if (t) localStorage.setItem(LS_TOKENS, JSON.stringify(t));
  else localStorage.removeItem(LS_TOKENS);
}

export function isAuthed(): boolean {
  return loadTokens() !== null;
}

export function logout(): void {
  saveTokens(null);
}

async function accessToken(): Promise<string | null> {
  let t = loadTokens();
  if (!t) return null;
  if (Date.now() < t.expires_at - 60_000) return t.access_token;

  const body = new URLSearchParams({
    client_id: SPOTIFY_CLIENT_ID,
    grant_type: 'refresh_token',
    refresh_token: t.refresh_token,
  });
  const res = await fetch('https://accounts.spotify.com/api/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) {
    saveTokens(null);
    return null;
  }
  const json = await res.json();
  t = {
    access_token: json.access_token,
    refresh_token: json.refresh_token ?? t.refresh_token,
    expires_at: Date.now() + json.expires_in * 1000,
  };
  saveTokens(t);
  return t.access_token;
}

/* ----------------------------- Web API ----------------------------------- */

async function api(path: string, init: RequestInit = {}): Promise<Response | null> {
  const token = await accessToken();
  if (!token) return null;
  return fetch(`https://api.spotify.com/v1${path}`, {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${token}` },
  });
}

/* --------------------------- Playback SDK -------------------------------- */

export class SpotifyPlayer implements Ducker {
  private player: any = null;
  private deviceId: string | null = null;
  private sdkState: NowPlaying | null = null;
  private pollTimer: number | null = null;
  private preDuckVolume = 0.8;
  private ducked = false;

  constructor(private events: SpotifyEvents = {}) {}

  get state(): NowPlaying | null {
    return this.sdkState;
  }

  async init(): Promise<void> {
    if (!isAuthed()) return;
    await this.loadSdk();
    this.startPolling();
  }

  private loadSdk(): Promise<void> {
    return new Promise((resolve) => {
      if (window.Spotify) {
        this.createPlayer().then(resolve);
        return;
      }
      window.onSpotifyWebPlaybackSDKReady = () => void this.createPlayer().then(resolve);
      const s = document.createElement('script');
      s.src = 'https://sdk.scdn.co/spotify-player.js';
      s.async = true;
      s.onerror = () => resolve(); // offline - remote control still works later
      document.head.appendChild(s);
    });
  }

  private async createPlayer(): Promise<void> {
    this.player = new window.Spotify.Player({
      name: 'Wayfarer',
      getOAuthToken: async (cb: (t: string) => void) => {
        const t = await accessToken();
        if (t) cb(t);
      },
      volume: 0.8,
    });

    this.player.addListener('ready', ({ device_id }: { device_id: string }) => {
      this.deviceId = device_id;
    });
    this.player.addListener('not_ready', () => {
      this.deviceId = null;
    });
    this.player.addListener('player_state_changed', (s: any) => {
      if (!s) {
        // SDK inactive; another device may be playing - poller covers that.
        return;
      }
      const track = s.track_window?.current_track;
      this.sdkState = {
        trackName: track?.name ?? '',
        artists: (track?.artists ?? []).map((a: any) => a.name).join(', '),
        albumArt: track?.album?.images?.[0]?.url ?? null,
        durationMs: s.duration,
        positionMs: s.position,
        paused: s.paused,
        playingHere: true,
        deviceName: 'Wayfarer',
      };
      this.events.onState?.(this.sdkState);
    });

    await this.player.connect();
  }

  /** One poll of the Web API for playback state (covers other devices too). */
  private async pollOnce(): Promise<void> {
    // While audio plays here, SDK events are authoritative and cheaper.
    if (this.sdkState?.playingHere && !this.sdkState.paused) return;
    const res = await api('/me/player');
    if (!res) return;
    if (res.status === 204) {
      this.events.onState?.(this.sdkState);
      return;
    }
    if (!res.ok) return;
    const j = await res.json();
    const item = j.item;
    if (!item) return;
    const state: NowPlaying = {
      trackName: item.name,
      artists: (item.artists ?? []).map((a: any) => a.name).join(', '),
      albumArt: item.album?.images?.[0]?.url ?? null,
      durationMs: item.duration_ms,
      positionMs: j.progress_ms ?? 0,
      paused: !j.is_playing,
      playingHere: j.device?.id === this.deviceId,
      deviceName: j.device?.name ?? null,
    };
    this.sdkState = state;
    this.events.onState?.(state);
  }

  private startPolling(): void {
    if (this.pollTimer !== null) return;
    this.pollTimer = window.setInterval(() => void this.pollOnce().catch(() => {}), 5000);
    void this.pollOnce().catch(() => {});
  }

  /* ------------------------- transport controls ------------------------- */

  async togglePlay(): Promise<void> {
    // Mobile browsers only allow audio started from a user gesture - activate
    // the SDK's media element inside this tap before anything async.
    try {
      this.player?.activateElement?.();
    } catch {
      /* older SDK builds lack activateElement */
    }

    if (this.sdkState?.playingHere && this.player) {
      await this.player.togglePlay();
      return;
    }
    const playing = this.sdkState && !this.sdkState.paused;
    if (playing) {
      await api('/me/player/pause', { method: 'PUT' });
      setTimeout(() => void this.pollOnce().catch(() => {}), 800);
      return;
    }
    // Something is paused on a known device - resume it where it lives.
    if (this.sdkState?.deviceName) {
      const res = await api('/me/player/play', { method: 'PUT' });
      if (res?.ok || res?.status === 204) {
        setTimeout(() => void this.pollOnce().catch(() => {}), 800);
        return;
      }
    }
    // Nothing active anywhere: start playback HERE from a cold start.
    await this.startHere();
  }

  /**
   * Cold-start playback on this device: transfer playback to the Wayfarer
   * player (resumes the user's last listening context), falling back to their
   * most recently played context/track when Spotify has nothing to resume.
   */
  private async startHere(): Promise<void> {
    if (!this.deviceId) return;

    const transfer = await api('/me/player', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_ids: [this.deviceId], play: true }),
    });
    if (transfer?.ok || transfer?.status === 202 || transfer?.status === 204) {
      setTimeout(() => void this.pollOnce().catch(() => {}), 1200);
      return;
    }

    const recent = await api('/me/player/recently-played?limit=1');
    if (recent?.ok) {
      const j = await recent.json();
      const ctx = j.items?.[0]?.context?.uri as string | undefined;
      const track = j.items?.[0]?.track?.uri as string | undefined;
      if (ctx || track) {
        await api(`/me/player/play?device_id=${this.deviceId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ctx ? { context_uri: ctx } : { uris: [track] }),
        });
        setTimeout(() => void this.pollOnce().catch(() => {}), 1200);
      }
    }
  }

  async next(): Promise<void> {
    if (this.sdkState?.playingHere && this.player) return this.player.nextTrack();
    await api('/me/player/next', { method: 'POST' });
  }

  async previous(): Promise<void> {
    if (this.sdkState?.playingHere && this.player) return this.player.previousTrack();
    await api('/me/player/previous', { method: 'POST' });
  }

  /** Move playback into this app (the "Play here" button). */
  async transferHere(): Promise<void> {
    if (!this.deviceId) return;
    await api('/me/player', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_ids: [this.deviceId], play: true }),
    });
  }

  async setVolume(v: number): Promise<void> {
    if (this.player && this.sdkState?.playingHere) {
      await this.player.setVolume(v);
    } else {
      await api(`/me/player/volume?volume_percent=${Math.round(v * 100)}`, { method: 'PUT' });
    }
    if (!this.ducked) this.preDuckVolume = v;
  }

  /* ------------------------------ ducking ------------------------------- */

  async duck(): Promise<void> {
    if (this.ducked) return;
    this.ducked = true;
    try {
      if (this.player && this.sdkState?.playingHere) {
        this.preDuckVolume = (await this.player.getVolume()) ?? this.preDuckVolume;
        await this.player.setVolume(Math.min(this.preDuckVolume, 0.15));
      } else if (this.sdkState && !this.sdkState.paused) {
        await api('/me/player/volume?volume_percent=15', { method: 'PUT' });
      }
    } catch {
      /* ducking is best-effort */
    }
  }

  async restore(): Promise<void> {
    if (!this.ducked) return;
    this.ducked = false;
    try {
      if (this.player && this.sdkState?.playingHere) {
        await this.player.setVolume(this.preDuckVolume);
      } else if (this.sdkState) {
        await api(`/me/player/volume?volume_percent=${Math.round(this.preDuckVolume * 100)}`, {
          method: 'PUT',
        });
      }
    } catch {
      /* ignore */
    }
  }
}
